/* eslint complexity: ["error", 40] */

import React, {Fragment, useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {withRouter, Redirect} from 'react-router-dom';
import {Container, Fade, Tabs, Tab, Modal, Button, Form} from 'react-bootstrap';
import {push} from 'connected-react-router';
import Dialog from 'react-bootstrap-dialog';
import CommonMetaTag from './common-meta-tag';
import PendingInvitationAlerts from './pending-invitation-alerts';
import {purchaseVideo, deleteBooking, deleteServiceBooking, openBookingForEdit} from '../actions/player';
import {cancelOpenMatch, getMyOpenMatches, leaveOpenMatch, updateOpenMatch} from '../api';
import {useTranslation} from 'react-i18next';
import {FaCalendarCheck, FaCheck, FaClipboardList, FaComments, FaEdit, FaHistory, FaTimes, FaTrashAlt, FaTrophy, FaUsers, FaVideo} from 'react-icons/fa';

const openMatchMatchesBooking = (match, booking) => (
  (match.bookingId && Number(booking.bookingId) === Number(match.bookingId)) ||
  (match.publicId && booking.openMatchPublicId === match.publicId) ||
  (
    Number(booking.partnerId) === Number(match.partnerId) &&
    String(booking.fieldName || '') === String(match.fieldName || '') &&
    new Date(booking.startDatetime).getTime() === new Date(match.startDatetime).getTime()
  )
);

const openMatchNeedsResultAction = match => (
  ['confirmed', 'completed'].includes(match.status) &&
  new Date(match.endDatetime) <= new Date() &&
  match.matchType === 'competitive' &&
  (!match.result || match.result.status !== 'accepted')
);

const PlayerBookings = ({
  token,
  profile,
  partners,
  bookings,
  deleteBooking,
  purchaseVideo,
  editBooking,
  goto
}) => {
  const {t, i18n} = useTranslation();
  const [summaryBooking, setSummaryBooking] = useState(null);
  const [purchaseConfirmation, setPurchaseConfirmation] = useState(null);
  const [openMatches, setOpenMatches] = useState([]);
  const [openMatchesLoading, setOpenMatchesLoading] = useState(false);
  const [openMatchDetail, setOpenMatchDetail] = useState(null);
  const [openMatchEdit, setOpenMatchEdit] = useState(null);
  const [openMatchActionLoading, setOpenMatchActionLoading] = useState(false);
  const [openMatchActionError, setOpenMatchActionError] = useState('');
  const [openMatchActionSuccess, setOpenMatchActionSuccess] = useState('');
  const nextBookings = useMemo(() => bookings.filter(b => new Date(b.startDatetime) > new Date()), [bookings]);
  const pastBookings = useMemo(() => bookings
    .filter(b => new Date(b.startDatetime) <= new Date())
    .filter(b => !openMatches.some(match => openMatchNeedsResultAction(match) && openMatchMatchesBooking(match, b)))
    .sort((a, b) => new Date(b.startDatetime) - new Date(a.startDatetime)), [bookings, openMatches]);
  const activeOpenMatches = useMemo(() => (
    openMatches
      .filter(match => (
        (['open', 'full'].includes(match.status) && new Date(match.startDatetime) > new Date()) ||
        openMatchNeedsResultAction(match)
      ))
      .sort((a, b) => new Date(a.startDatetime) - new Date(b.startDatetime))
  ), [openMatches]);
  const confirmedOpenMatchBookings = useMemo(() => (
    openMatches
      .filter(match => match.status === 'confirmed' && new Date(match.startDatetime) > new Date())
      .filter(match => !bookings.some(booking => openMatchMatchesBooking(match, booking)))
      .sort((a, b) => new Date(a.startDatetime) - new Date(b.startDatetime))
  ), [bookings, openMatches]);
  const pastOpenMatchBookings = useMemo(() => (
    openMatches
      .filter(match => ['confirmed', 'completed'].includes(match.status) && new Date(match.endDatetime) <= new Date())
      .filter(match => !openMatchNeedsResultAction(match))
      .filter(match => !bookings.some(booking => openMatchMatchesBooking(match, booking)))
      .sort((a, b) => new Date(b.startDatetime) - new Date(a.startDatetime))
  ), [bookings, openMatches]);
  const openMatchResults = useMemo(() => (
    openMatches
      .filter(match => match.matchType === 'competitive' && match.result)
      .sort((a, b) => new Date(b.startDatetime) - new Date(a.startDatetime))
  ), [openMatches]);

  const loadOpenMatches = React.useCallback(async mounted => {
      if (!token) return;
      setOpenMatchesLoading(true);
      const res = await getMyOpenMatches(token);
      if (mounted.current && res.status === 200) {
        setOpenMatches(res.json.openMatches || []);
      }
      if (mounted.current) {
        setOpenMatchesLoading(false);
      }
  }, [token]);

  useEffect(() => {
    const mounted = {current: true};
    loadOpenMatches(mounted);
    return () => {
      mounted.current = false;
    };
  }, [loadOpenMatches]);

  if (token === null) {
    return (
      <Redirect to="/login"/>
    );
  }

  let dialog = null;
  const confirmPurchase = (slots, price) => () => {
    setPurchaseConfirmation({slots, price});
  };

  const purchaseConfirmed = () => {
    if (!purchaseConfirmation) return;
    purchaseVideo(purchaseConfirmation.slots);
    setPurchaseConfirmation(null);
  };

  const deleteBookingDialog = b => () => {
    dialog.show({
      title: t('BOOKING_DELETE'),
      body: b.groupBooking ? t('BOOKING_DELETE_CONFIRM_GROUP') : t('BOOKING_DELETE_CONFIRM'),
      actions: [
        Dialog.Action(
          t('CONFIRM_DELETE'),
          () => deleteBooking(b.groupBooking || !b.multiBooking ? b.bookingId : b.users.filter(u => u.uid === profile.uid)[0].id, b.partnerId, b.sport === undefined, !b.groupBooking && b.multiBooking, partners.find(p => p.uid === b.partnerId).phoneNumber),
          'btn btn-secondary'
        ),
        Dialog.Action(
          t('DO_NOT_DELETE'),
          null,
          'btn btn_red_outline_rp'
        )
      ],
      bsSize: 'small',
      onHide: dialog => {
        dialog.hide();
      }
    });
  };

  const getPurchaseStatusText = b => {
    if (b.videoPurchasable && !b.videoBought && !b.videoReady) {
      return t('VIDEO_STATUS_PURCHASABLE', {price: b.videoPrice});
    }

    if (b.videoBought && !b.videoReady) {
      return t('VIDEO_STATUS_PROCESSING');
    }

    if (b.videoBought && b.videoReady) {
      return t('VIDEO_STATUS_DOWNLOADABLE');
    }

    return '';
  };

  const inviteRole = inviteType => {
    if (inviteType === 0 || inviteType === 'referent') return t('SKYWORK_BOOKING_ORGANIZER');
    if (inviteType === 1 || inviteType === 'partecipant') return t('PARTECIPANT');
    return t('GUEST');
  };

  const bookingPaidTotal = b => (b.users || []).reduce((total, user) => total + Number(user.amountPaid || 0), 0);

  const openMatchStatusLabel = match => {
    if (!match) return '';
    if (match.status === 'confirmed') return t('OPEN_MATCH_STATUS_CONFIRMED_BOOKED');
    if (match.status === 'cancelled') return t('OPEN_MATCH_STATUS_CANCELLED');
    if (match.status === 'expired') return t('OPEN_MATCH_STATUS_EXPIRED');
    if (match.status === 'full') return t('OPEN_MATCH_STATUS_FULL_CONFIRMING');
    return t('OPEN_MATCH_STATUS_OPEN');
  };

  const openMatchCreditLabel = match => {
    const amount = match && match.credit && match.credit.requiredEur ? Number(match.credit.requiredEur).toFixed(2) : null;
    if (!amount) return '';
    if (match.status === 'confirmed') return t('OPEN_MATCH_FEE_CHARGED', {amount});
    if (match.status === 'cancelled') return t('OPEN_MATCH_CREDIT_RELEASED', {amount});
    return t('OPEN_MATCH_CREDIT_HELD', {amount});
  };

  const openMatchDate = match => new Date(match.startDatetime).toLocaleDateString(i18n.language, {weekday: 'long', year: 'numeric', month: 'numeric', day: 'numeric'});
  const openMatchTime = match => t('FROM_TO_TIME', {
    fromTime: new Date(match.startDatetime).toLocaleTimeString(i18n.language, {hour: '2-digit', minute: '2-digit'}),
    toTime: new Date(match.endDatetime).toLocaleTimeString(i18n.language, {hour: '2-digit', minute: '2-digit'})
  });
  const resultScore = result => (result && Array.isArray(result.sets) ? result.sets.map(set => `${set.team1}-${set.team2}`).join(' ') : '');
  const resultStatusText = match => {
    if (!match || !match.result) return t('OPEN_MATCH_RESULT_NOT_ENTERED');
    if (match.result.status === 'pending') return t('OPEN_MATCH_RESULT_PENDING');
    if (match.result.status === 'rejected') return t('OPEN_MATCH_RESULT_REJECTED');
    return t('OPEN_MATCH_RESULT_CONFIRMED');
  };
  const resultOutcome = match => {
    const result = match && match.result;
    if (!result || result.status !== 'accepted') return {label: resultStatusText(match), won: null};
    const currentUid = Number(profile.uid);
    const team1 = (result.team1Uids || []).map(Number);
    const team2 = (result.team2Uids || []).map(Number);
    const userTeam = team1.includes(currentUid) ? 1 : team2.includes(currentUid) ? 2 : null;
    if (!userTeam) return {label: t('OPEN_MATCH_RESULT_CONFIRMED'), won: null};
    const won = Number(result.winnerTeam) === userTeam;
    return {label: won ? t('OPEN_MATCH_RESULT_WIN') : t('OPEN_MATCH_RESULT_LOSS'), won};
  };
  const openMatchForBooking = booking => openMatches.find(match => (
    ['confirmed', 'completed'].includes(match.status) &&
    openMatchMatchesBooking(match, booking)
  ));
  const openMatchResultActionLabel = match => {
    if (!match || !match.result) return t('OPEN_MATCH_UPDATE_SCORE');
    if (match.result.status === 'pending') return t('OPEN_MATCH_CONFIRM_RESULT');
    return t('OPEN_MATCH_VIEW_RESULT');
  };
  const participantNamesForTeam = (match, uids) => {
    const participants = match.participants || [];
    return (uids || []).map(uid => {
      const participant = participants.find(item => Number(item.uid) === Number(uid));
      return participant ? participant.fullName || participant.name || `Player #${uid}` : `Player #${uid}`;
    }).join(', ');
  };

  const openOpenMatchDetail = match => {
    setOpenMatchActionError('');
    setOpenMatchActionSuccess('');
    setOpenMatchDetail(match);
    setOpenMatchEdit({
      matchType: match.matchType || 'friendly',
      playersRequired: match.playersRequired || 4,
      minLevel: match.minLevel || '',
      maxLevel: match.maxLevel || '',
      title: match.title || '',
      notes: match.notes || '',
      gender: match.gender || 'any'
    });
  };

  const refreshOpenMatchDetail = updatedMatch => {
    setOpenMatches(matches => matches.map(match => match.id === updatedMatch.id ? updatedMatch : match));
    setOpenMatchDetail(updatedMatch);
  };

  const reloadOpenMatchesAfterAction = async () => {
    const mounted = {current: true};
    await loadOpenMatches(mounted);
  };

  const saveOpenMatchChanges = async () => {
    if (!openMatchDetail || !openMatchEdit) return;
    setOpenMatchActionError('');
    setOpenMatchActionSuccess('');
    setOpenMatchActionLoading(true);
    const res = await updateOpenMatch(openMatchDetail.id, openMatchEdit, token);
    if (res.status === 200) {
      refreshOpenMatchDetail(res.json.openMatch);
      setOpenMatchActionSuccess(t('CHANGES_SAVED_SUCCESSFULLY'));
    } else {
      setOpenMatchActionError((res.json && (res.json.error || res.json.reason)) || t('OPEN_MATCH_SAVE_ERROR'));
    }
    setOpenMatchActionLoading(false);
  };

  const cancelOpenMatchFromDetail = async () => {
    if (!openMatchDetail) return;
    setOpenMatchActionError('');
    setOpenMatchActionSuccess('');
    setOpenMatchActionLoading(true);
    const res = await cancelOpenMatch(openMatchDetail.id, token);
    if (res.status === 200) {
      refreshOpenMatchDetail(res.json.openMatch);
      await reloadOpenMatchesAfterAction();
    } else {
      setOpenMatchActionError((res.json && (res.json.error || res.json.reason)) || t('OPEN_MATCH_CANCEL_ERROR'));
    }
    setOpenMatchActionLoading(false);
  };

  const leaveOpenMatchFromDetail = async () => {
    if (!openMatchDetail) return;
    setOpenMatchActionError('');
    setOpenMatchActionSuccess('');
    setOpenMatchActionLoading(true);
    const res = await leaveOpenMatch(openMatchDetail.id, token);
    if (res.status === 200) {
      setOpenMatchDetail(null);
      await reloadOpenMatchesAfterAction();
    } else {
      setOpenMatchActionError((res.json && (res.json.error || res.json.reason)) || t('OPEN_MATCH_LEAVE_ERROR'));
    }
    setOpenMatchActionLoading(false);
  };

  const canManageOpenMatch = match => match && Number(match.creatorUid) === Number(profile.uid) && ['open', 'full'].includes(match.status);
  const canLeaveOpenMatch = match => match && Number(match.creatorUid) !== Number(profile.uid) && ['open', 'full'].includes(match.status);
  const canChatOpenMatch = match => match && ['open', 'full', 'confirmed'].includes(match.status);

  return (
    <Fragment>
      <CommonMetaTag/>
      <Fade in appear className="bg_white" style={{transition: 'opacity 0.30s linear', minHeight: 1000}}>
        <Container fluid className="presentation-container">
          <div className="page-header header-filter clear-filter dashboard_hero_rp" data-parallax="true">
            <div className="dashboard_hero_overlay_rp"/>
          </div>
          <div className="main main_raised_rp">
            <div className="section">
              <div className="container">
                <PendingInvitationAlerts/>
                <div className="row mb-4">
                  <div className="col-12">
                    <div><h4 className="section_title_neg_rp">{t('BOOKINGS')}</h4></div>
                    <Tabs defaultActiveKey={'next_bookings'} className="myvideo-tabs booking-section-tabs" style={{marginTop: 20}}>
                      <Tab eventKey="next_bookings" title={<span className="section-tab-label"><FaCalendarCheck aria-hidden="true"/>{t('NEXT')}</span>} tabClassName="myvideo-tab">
                        <div className="row" style={{marginTop: 64}}>
                          {
                            nextBookings.length !== 0 || confirmedOpenMatchBookings.length !== 0 ?
                            <Fragment>
                            {nextBookings.map(b =>
                              (
                                <div key={b.bookingId} className="col-md-12 col-lg-6 mb-sm-4 card-group">
                                  <div className="card reservation_bg_rp">
                                    <div className="card-horizontal">
                                      <div className="card_image_rp col-sm-3 col-lg-4 pl-0 pr-0">
                                        <div className="card_image_overlay_rp">
                                          <div className="card_image_icon_overlay_rp"/>
                                        </div>
                                      </div>
                                      <div className="card-body d-flex flex-column col-sm-9 col-lg-8">
                                        <h5 className="card-title">{b.partnerName}</h5>
                                        <ul className="list-unstyled">
                                          <li>{b.fieldName || b.serviceName}</li>
                                          <li>{new Date(b.startDatetime).toLocaleDateString(i18n.language, {weekday: 'long', year: 'numeric', month: 'numeric', day: 'numeric'})}</li>
                                          <li>{t('FROM_TO_TIME', {fromTime: new Date(b.startDatetime).toLocaleTimeString(i18n.language, {hour: '2-digit', minute: '2-digit'}), toTime: new Date(b.endDatetime).toLocaleTimeString(i18n.language, {hour: '2-digit', minute: '2-digit'})})}</li>
                                          <li className="font-italic">{getPurchaseStatusText(b)}</li>
                                        </ul>
                                        <div className="mt-auto mb-2 reservation_buttons_rp">
                                          {
                                            b.videoPurchasable && !b.videoBought ?
                                            <button type="button" className="btn home-skywork-action home-skywork-action--video" onClick={confirmPurchase(b.slots, b.videoPrice)}><FaVideo aria-hidden="true"/>{t('PURCHASE_VIDEO_BUTTON')}</button> :
                                            null
                                          }
                                          {b.fieldName ? <button type="button" className="btn home-skywork-action home-skywork-action--edit" onClick={() => editBooking(b)}><FaEdit aria-hidden="true"/>{t('SKYWORK_EDIT_BOOKING')}</button> : null}
                                          <button type="button" className="btn home-skywork-action home-skywork-action--chat" onClick={goto(`/messages?booking=${b.bookingId}`)}><FaComments aria-hidden="true"/>{t('MESSAGES')}</button>
                                          <button type="button" className="btn home-skywork-action home-skywork-action--secondary" onClick={() => setSummaryBooking(b)}><FaClipboardList aria-hidden="true"/>{t('SKYWORK_BOOKING_SUMMARY')}</button>
                                          <button type="button" className="btn home-skywork-action home-skywork-action--danger" onClick={deleteBookingDialog(b)}><FaTrashAlt aria-hidden="true"/>{t('BOOKING_DELETE')}</button>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>))}
                            {confirmedOpenMatchBookings.map(match => (
                              <div key={`confirmed-open-match-${match.id}`} className="col-md-12 col-lg-6 mb-sm-4 card-group">
                                <div className="card reservation_bg_rp player-bookings-open-match-card">
                                  <div className="card-body d-flex flex-column">
                                    <div className="player-bookings-open-match-status">
                                      <span>{openMatchStatusLabel(match)}</span>
                                      <strong>{match.playersJoined}/{match.playersRequired}</strong>
                                    </div>
                                    <h5 className="card-title">{match.partnerName}</h5>
                                    <ul className="list-unstyled">
                                      <li>{match.fieldName} · {match.sport}</li>
                                      <li>{openMatchDate(match)}</li>
                                      <li>{openMatchTime(match)}</li>
                                      <li>{t('OPEN_MATCH_BOOKING_CONFIRMED')}</li>
                                      {openMatchCreditLabel(match) ? <li className="font-italic">{openMatchCreditLabel(match)}</li> : null}
                                    </ul>
                                    <div className="mt-auto mb-2 reservation_buttons_rp">
                                      <button type="button" className="btn home-skywork-action home-skywork-action--secondary" onClick={goto(`/matches/${match.publicId}`)}><FaClipboardList aria-hidden="true"/>{t('VIEW_MATCH')}</button>
                                      <button type="button" className="btn home-skywork-action home-skywork-action--chat" onClick={goto(`/messages?openMatch=${match.id}`)}><FaComments aria-hidden="true"/>{t('MESSAGES')}</button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                            </Fragment>
                              :
                              <div className="col-md-12 col-lg-6 mb-sm-4 card-group">
                                <div className="card empty_slot reservation_bg_rp">
                                  <div className="card-horizontal">
                                    <div className="card_image_rp col-sm-3 col-lg-4 pl-0 pr-0">
                                      <div className="card_image_overlay_rp">
                                        <div className="card_image_icon_overlay_rp"/>
                                      </div>
                                    </div>
                                    <div className="card-body d-flex flex-column col-sm-9 col-lg-8">
                                      <h5 className="card-title">{t('PLAYER_HOME_NO_RESERVATION_TITLE')}</h5>
                                      <ul className="list-unstyled">
                                        <li>{t('PLAYER_HOME_NO_RESERVATION_DESC')}</li>
                                      </ul>
                                      <div className="mt-auto mb-2 reservation_buttons_rp">
                                        <button type="button" className="btn btn_red_rp" onClick={goto('player-show-sport-centers')}>{t('SPORT_CENTERS')}</button>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                          }
                        </div>
                      </Tab>
                      <Tab eventKey="open_matches" title={<span className="section-tab-label"><FaUsers aria-hidden="true"/>{t('OPEN_MATCHES')}</span>} tabClassName="myvideo-tab">
                        <div className="player-bookings-open-matches" style={{marginTop: 64}}>
                          <div className="player-bookings-open-matches-head">
                            <span>{t('OPEN_MATCHES')}</span>
                            <p>{t('MY_BOOKINGS_OPEN_MATCHES_HELP')}</p>
                          </div>
                          {openMatchesLoading ? <div className="open-matches-empty">{t('OPEN_MATCHES_LOADING')}</div> : null}
                          {!openMatchesLoading && activeOpenMatches.length === 0 ? (
                            <div className="open-matches-empty">{t('OPEN_MATCHES_EMPTY_MANAGE')}</div>
                          ) : null}
                          <div className="row">
                            {activeOpenMatches.map(match => {
                              const needsResultAction = openMatchNeedsResultAction(match);
                              return (
                                <div key={match.id} className="col-md-12 col-lg-6 mb-sm-4 card-group">
                                  <div className="card reservation_bg_rp player-bookings-open-match-card">
                                    <div className="card-body d-flex flex-column">
                                      <div className="player-bookings-open-match-status">
                                        <span>{needsResultAction ? resultStatusText(match) : openMatchStatusLabel(match)}</span>
                                        <strong>{match.playersJoined}/{match.playersRequired}</strong>
                                      </div>
                                      <h5 className="card-title">{match.partnerName}</h5>
                                      <ul className="list-unstyled">
                                        <li>{match.fieldName} · {match.sport}</li>
                                        <li>{openMatchDate(match)}</li>
                                        <li>{openMatchTime(match)}</li>
                                        <li>{t('LEVEL')} {match.minLevel || '-'} - {match.maxLevel || '-'}</li>
                                        {needsResultAction ? <li className="font-weight-bold">{t('OPEN_MATCH_RESULT_REQUIRED_BEFORE_ARCHIVE')}</li> : null}
                                        {openMatchCreditLabel(match) ? <li className="font-italic">{openMatchCreditLabel(match)}</li> : null}
                                      </ul>
                                      <div className="mt-auto mb-2 reservation_buttons_rp">
                                        {needsResultAction ? (
                                          <button type="button" className="btn home-skywork-action home-skywork-action--open-match" onClick={goto(`/matches/${match.publicId}`)}><FaTrophy aria-hidden="true"/>{openMatchResultActionLabel(match)}</button>
                                        ) : (
                                          <button type="button" className="btn home-skywork-action home-skywork-action--secondary" onClick={() => openOpenMatchDetail(match)}><FaClipboardList aria-hidden="true"/>{t('MANAGE_MATCH')}</button>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </Tab>
                      <Tab eventKey="open_match_results" title={<span className="section-tab-label"><FaTrophy aria-hidden="true"/>{t('LATEST_RESULTS')}</span>} tabClassName="myvideo-tab">
                        <div className="player-bookings-open-matches" style={{marginTop: 64}}>
                          <div className="player-bookings-open-matches-head">
                            <span>{t('LATEST_RESULTS')}</span>
                            <p>{t('OPEN_MATCH_RESULTS_HELP')}</p>
                          </div>
                          {openMatchesLoading ? <div className="open-matches-empty">{t('RESULTS_LOADING')}</div> : null}
                          {!openMatchesLoading && openMatchResults.length === 0 ? (
                            <div className="open-matches-empty">{t('OPEN_MATCH_RESULTS_EMPTY')}</div>
                          ) : null}
                          <div className="row">
                            {openMatchResults.map(match => {
                              const outcome = resultOutcome(match);
                              const result = match.result || {};
                              return (
                                <div key={`result-${match.id}`} className="col-md-12 col-lg-6 mb-sm-4 card-group">
                                  <div className="card reservation_bg_rp player-bookings-open-match-card player-bookings-result-card">
                                    <div className="card-body d-flex flex-column">
                                      <div className={`player-bookings-open-match-status ${outcome.won === true ? 'is-win' : outcome.won === false ? 'is-loss' : ''}`}>
                                        <span>{outcome.label}</span>
                                        <strong>{resultScore(result) || resultStatusText(match)}</strong>
                                      </div>
                                      <h5 className="card-title">{match.partnerName}</h5>
                                      <ul className="list-unstyled">
                                        <li>{match.fieldName} · {match.sport}</li>
                                        <li>{openMatchDate(match)}</li>
                                        <li>{openMatchTime(match)}</li>
                                        <li>Team 1: {participantNamesForTeam(match, result.team1Uids)}</li>
                                        <li>Team 2: {participantNamesForTeam(match, result.team2Uids)}</li>
                                        <li>{result.ratingApplied ? 'Ranking aggiornato' : resultStatusText(match)}</li>
                                      </ul>
                                      <div className="mt-auto mb-2 reservation_buttons_rp">
                                        <button type="button" className="btn home-skywork-action home-skywork-action--secondary" onClick={goto(`/matches/${match.publicId}`)}><FaClipboardList aria-hidden="true"/>{t('VIEW_MATCH')}</button>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </Tab>
                      <Tab eventKey="past_bookings" title={<span className="section-tab-label"><FaHistory aria-hidden="true"/>{t('PAST')}</span>} tabClassName="myvideo-tab">
                        <p style={{marginTop: 20}}>{t('BOOKINGS_INFO')}</p>
                        <div className="row">
                          {pastBookings.map(b => {
                            const relatedOpenMatch = openMatchForBooking(b);
                            return (
                              <div key={b.bookingId} className="col-md-12 col-lg-6 mb-sm-4 card-group">
                                <div className="card reservation_bg_rp">
                                  <div className="card-horizontal">
                                    <div className="card_image_rp col-sm-3 col-lg-4 pl-0 pr-0">
                                      <div className="card_image_overlay_rp">
                                        <div className="card_image_icon_overlay_rp"/>
                                      </div>
                                    </div>
                                    <div className="card-body d-flex flex-column col-sm-9 col-lg-8">
                                      <h5 className="card-title">{b.partnerName}</h5>
                                      <ul className="list-unstyled">
                                        <li>{b.fieldName || b.serviceName}</li>
                                        <li>{new Date(b.startDatetime).toLocaleDateString(i18n.language, {weekday: 'long', year: 'numeric', month: 'numeric', day: 'numeric'})}</li>
                                        <li>{t('FROM_TO_TIME', {fromTime: new Date(b.startDatetime).toLocaleTimeString(i18n.language, {hour: '2-digit', minute: '2-digit'}), toTime: new Date(b.endDatetime).toLocaleTimeString(i18n.language, {hour: '2-digit', minute: '2-digit'})})}</li>
                                        {relatedOpenMatch ? <li className="font-weight-bold">{t('OPEN_MATCH')} · {openMatchStatusLabel(relatedOpenMatch)}</li> : null}
                                        {relatedOpenMatch ? <li>{resultStatusText(relatedOpenMatch)}</li> : null}
                                        <li className="font-italic">{getPurchaseStatusText(b)}</li>
                                      </ul>
                                      <div className="mt-auto mb-2 reservation_buttons_rp">
                                        {relatedOpenMatch ? (
                                          <button type="button" className="btn home-skywork-action home-skywork-action--open-match" onClick={goto(`/matches/${relatedOpenMatch.publicId}`)}><FaTrophy aria-hidden="true"/>{openMatchResultActionLabel(relatedOpenMatch)}</button>
                                        ) : null}
                                        {
                                          b.videoPurchasable && !b.videoBought ?
                                          <button type="button" className="btn home-skywork-action home-skywork-action--video" onClick={confirmPurchase(b.slots, b.videoPrice)}><FaVideo aria-hidden="true"/>{t('PURCHASE_VIDEO_BUTTON')}</button> :
                                          null
                                        }
                                        <button type="button" className="btn home-skywork-action home-skywork-action--chat" onClick={goto(`/messages?booking=${b.bookingId}`)}><FaComments aria-hidden="true"/>{t('MESSAGES')}</button>
                                        <button type="button" className="btn home-skywork-action home-skywork-action--secondary" onClick={() => setSummaryBooking(b)}><FaClipboardList aria-hidden="true"/>{t('SKYWORK_BOOKING_SUMMARY')}</button>
                                        <button type="button" className="btn home-skywork-action home-skywork-action--danger" onClick={deleteBookingDialog(b)}><FaTrashAlt aria-hidden="true"/>{t('BOOKING_DELETE')}</button>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                          {pastOpenMatchBookings.map(match => (
                            <div key={`past-open-match-${match.id}`} className="col-md-12 col-lg-6 mb-sm-4 card-group">
                              <div className="card reservation_bg_rp player-bookings-open-match-card">
                                <div className="card-body d-flex flex-column">
                                  <div className="player-bookings-open-match-status">
                                    <span>{openMatchStatusLabel(match)}</span>
                                    <strong>{match.playersJoined}/{match.playersRequired}</strong>
                                  </div>
                                  <h5 className="card-title">{match.partnerName}</h5>
                                  <ul className="list-unstyled">
                                    <li>{match.fieldName} · {match.sport}</li>
                                    <li>{openMatchDate(match)}</li>
                                    <li>{openMatchTime(match)}</li>
                                    <li>{t('OPEN_MATCH_CLOSED')}</li>
                                    {openMatchCreditLabel(match) ? <li className="font-italic">{openMatchCreditLabel(match)}</li> : null}
                                  </ul>
                                  <div className="mt-auto mb-2 reservation_buttons_rp">
                                    <button type="button" className="btn home-skywork-action home-skywork-action--open-match" onClick={goto(`/matches/${match.publicId}`)}><FaTrophy aria-hidden="true"/>{openMatchResultActionLabel(match)}</button>
                                    <button type="button" className="btn home-skywork-action home-skywork-action--chat" onClick={goto(`/messages?openMatch=${match.id}`)}><FaComments aria-hidden="true"/>{t('MESSAGES')}</button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </Tab>
                    </Tabs>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </Fade>
      <Dialog
        ref={el => {
          dialog = el;
        }}/>
      <Modal
        centered
        show={purchaseConfirmation !== null}
        onHide={() => setPurchaseConfirmation(null)}
        dialogClassName="skywork-video-purchase-modal"
      >
        <Modal.Header closeButton>
          <div className="skywork-video-purchase-modal__heading">
            <span className="skywork-video-purchase-modal__icon"><FaVideo aria-hidden="true"/></span>
            <div>
              <small>{t('REPLAYER_VIDEO')}</small>
              <Modal.Title>{t('CONFIRM_PURCHASE_TITLE')}</Modal.Title>
            </div>
          </div>
        </Modal.Header>
        <Modal.Body>
          <div className="skywork-video-purchase-modal__summary">
            <span>{t('VIDEO_AMOUNT')}</span>
            <strong>€ {Number(purchaseConfirmation ? purchaseConfirmation.price : 0).toFixed(2)}</strong>
          </div>
          <p>{t('CONFIRM_PURCHASE_BODY', {price: purchaseConfirmation ? purchaseConfirmation.price : 0})}</p>
          <div className="skywork-video-purchase-modal__notice">
            <FaVideo aria-hidden="true"/>
            <span>{t('VIDEO_PURCHASE_CONFIRM_HELP')}</span>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button type="button" className="skywork-video-purchase-modal__cancel" onClick={() => setPurchaseConfirmation(null)}>
            <FaTimes aria-hidden="true"/>{t('CANCEL')}
          </Button>
          <Button type="button" className="skywork-video-purchase-modal__confirm" onClick={purchaseConfirmed}>
            <FaCheck aria-hidden="true"/>{t('PURCHASE_VIDEO_BUTTON')}
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal show={summaryBooking !== null} onHide={() => setSummaryBooking(null)} dialogClassName="skywork-booking-summary-modal">
        <Modal.Header closeButton>
          <Modal.Title>{t('SKYWORK_BOOKING_SUMMARY')}</Modal.Title>
        </Modal.Header>
        {summaryBooking ? <Modal.Body>
          <div className="skywork-booking-summary-head">
            <strong>{summaryBooking.partnerName}</strong>
            <span>{summaryBooking.fieldName || summaryBooking.serviceName}{summaryBooking.sport ? ` · ${summaryBooking.sport}` : ''}</span>
            <span>{new Date(summaryBooking.startDatetime).toLocaleDateString(i18n.language, {weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'})}</span>
            <span>{t('FROM_TO_TIME', {fromTime: new Date(summaryBooking.startDatetime).toLocaleTimeString(i18n.language, {hour: '2-digit', minute: '2-digit'}), toTime: new Date(summaryBooking.endDatetime).toLocaleTimeString(i18n.language, {hour: '2-digit', minute: '2-digit'})})}</span>
          </div>
          <h6>{t('SKYWORK_BOOKING_PARTICIPANTS')}</h6>
          <div className="skywork-booking-summary-users">
            {(summaryBooking.users || []).map(user => <div key={user.id} className="skywork-booking-summary-user">
              <span><strong>{user.bookingOwner || `${user.partnerBookingName || ''} ${user.partnerBookingSurname || ''}`.trim()}</strong><small>{inviteRole(user.inviteType)}</small></span>
              <b>{Number(user.amountPaid || 0).toFixed(2)} €</b>
            </div>)}
          </div>
          <div className="skywork-booking-summary-costs">
            <span>{t('SKYWORK_FIXED_COSTS_TOTAL')} <b>{Number(summaryBooking.costs || 0).toFixed(2)} €</b></span>
            <span>{t('SKYWORK_TOTAL_PAID')} <strong>{bookingPaidTotal(summaryBooking).toFixed(2)} €</strong></span>
          </div>
        </Modal.Body> : null}
        <Modal.Footer>
          <Button variant="outline-primary" onClick={() => setSummaryBooking(null)}>{t('CLOSE_BUTTON')}</Button>
        </Modal.Footer>
      </Modal>
      <Modal show={openMatchDetail !== null} onHide={() => setOpenMatchDetail(null)} centered dialogClassName="open-match-ranking-modal">
        <Modal.Header closeButton className="open-match-ranking-modal-header">
          <div>
            <span>{t('OPEN_MATCH_KICKER')}</span>
            <Modal.Title>{openMatchDetail ? openMatchStatusLabel(openMatchDetail) : ''}</Modal.Title>
          </div>
        </Modal.Header>
        {openMatchDetail && openMatchEdit ? (
          <Modal.Body className="open-match-ranking-modal-body">
            <div className="open-match-create-summary">
              <strong>{openMatchDetail.partnerName}</strong>
              <small>{openMatchDetail.fieldName} · {openMatchDetail.sport}</small>
              <small>{openMatchDate(openMatchDetail)} · {openMatchTime(openMatchDetail)}</small>
              <small>{openMatchDetail.playersJoined}/{openMatchDetail.playersRequired} partecipanti · Livello {openMatchDetail.minLevel || '-'} - {openMatchDetail.maxLevel || '-'}</small>
            </div>
            {openMatchActionError ? <div className="open-match-detail-error">{openMatchActionError}</div> : null}
            {openMatchActionSuccess ? <div className="open-match-detail-success">{openMatchActionSuccess}</div> : null}
            <div className="open-match-detail-participants">
              <strong>{t('PARTICIPANTS')}</strong>
              {(openMatchDetail.participants || []).map(participant => (
                <div key={participant.uid || participant.id} className="open-match-detail-participant">
                  <span>{participant.fullName || participant.name || participant.email || `Player #${participant.uid}`}</span>
                  {participant.uid === openMatchDetail.creatorUid ? <small>{t('OPEN_MATCH_ORGANIZER')}</small> : <small>{t('PARTICIPANT')}</small>}
                </div>
              ))}
            </div>
            {canManageOpenMatch(openMatchDetail) ? (
              <div className="open-match-detail-edit">
                <Form.Group>
                  <Form.Label>{t('TITLE')}</Form.Label>
                  <Form.Control type="text" maxLength={120} value={openMatchEdit.title} onChange={event => setOpenMatchEdit({...openMatchEdit, title: event.target.value})}/>
                </Form.Group>
                <Form.Group>
                  <Form.Label>{t('NOTES')}</Form.Label>
                  <Form.Control as="textarea" rows={3} maxLength={500} value={openMatchEdit.notes} onChange={event => setOpenMatchEdit({...openMatchEdit, notes: event.target.value})}/>
                </Form.Group>
                <div className="open-match-create-choice">
                  <button type="button" className={openMatchEdit.matchType === 'friendly' ? 'active' : ''} onClick={() => setOpenMatchEdit({...openMatchEdit, matchType: 'friendly'})}>{t('OPEN_MATCH_TYPE_FRIENDLY')}</button>
                  <button type="button" className={openMatchEdit.matchType === 'competitive' ? 'active' : ''} onClick={() => setOpenMatchEdit({...openMatchEdit, matchType: 'competitive'})}>{t('OPEN_MATCH_TYPE_COMPETITIVE')}</button>
                </div>
                <div className="open-match-create-choice">
                  <button type="button" className={Number(openMatchEdit.playersRequired) === 2 ? 'active' : ''} disabled={openMatchDetail.playersJoined > 2} onClick={() => setOpenMatchEdit({...openMatchEdit, playersRequired: 2})}>{t('PLAYERS_COUNT', {count: 2})}</button>
                  <button type="button" className={Number(openMatchEdit.playersRequired) === 4 ? 'active' : ''} onClick={() => setOpenMatchEdit({...openMatchEdit, playersRequired: 4})}>{t('PLAYERS_COUNT', {count: 4})}</button>
                </div>
                <div className="open-match-detail-levels">
                  <Form.Group>
                    <Form.Label>{t('OPEN_MATCH_MIN_LEVEL')}</Form.Label>
                    <Form.Control type="number" min="1" max="10" step="0.1" value={openMatchEdit.minLevel} onChange={event => setOpenMatchEdit({...openMatchEdit, minLevel: event.target.value})}/>
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>{t('OPEN_MATCH_MAX_LEVEL')}</Form.Label>
                    <Form.Control type="number" min="1" max="10" step="0.1" value={openMatchEdit.maxLevel} onChange={event => setOpenMatchEdit({...openMatchEdit, maxLevel: event.target.value})}/>
                  </Form.Group>
                </div>
              </div>
            ) : (
              <div className="open-match-ranking-intro">
                {openMatchDetail.status === 'confirmed' ? t('OPEN_MATCH_CONFIRMED_BOOKING_HELP') : null}
                {openMatchDetail.status === 'cancelled' ? t('OPEN_MATCH_CANCELLED_CREDIT_HELP') : null}
                {canLeaveOpenMatch(openMatchDetail) ? t('OPEN_MATCH_LEAVE_HELP') : null}
              </div>
            )}
          </Modal.Body>
        ) : null}
        <Modal.Footer className="open-match-ranking-modal-footer">
          <Button variant="outline-secondary" className="open-match-ranking-cancel" onClick={() => setOpenMatchDetail(null)}>{t('CLOSE_BUTTON')}</Button>
          {canLeaveOpenMatch(openMatchDetail) ? (
            <Button type="button" className="home-skywork-action home-skywork-action--danger" disabled={openMatchActionLoading} onClick={leaveOpenMatchFromDetail}>{t('OPEN_MATCH_LEAVE')}</Button>
          ) : null}
          {canManageOpenMatch(openMatchDetail) ? (
            <Button type="button" className="home-skywork-action home-skywork-action--danger" disabled={openMatchActionLoading} onClick={cancelOpenMatchFromDetail}>{t('OPEN_MATCH_CANCEL')}</Button>
          ) : null}
          {canChatOpenMatch(openMatchDetail) ? (
            <Button type="button" className="home-skywork-action home-skywork-action--chat" onClick={goto(`/messages?openMatch=${openMatchDetail.id}`)}><FaComments aria-hidden="true"/>{t('MESSAGES')}</Button>
          ) : null}
          {canManageOpenMatch(openMatchDetail) ? (
            <Button type="button" className="profile-auth-action profile-auth-action--save" disabled={openMatchActionLoading} onClick={saveOpenMatchChanges}>{openMatchActionLoading ? t('SAVING') : t('SAVE_CHANGES')}</Button>
          ) : null}
        </Modal.Footer>
      </Modal>
    </Fragment>
  );
};

PlayerBookings.propTypes = {
  token: PropTypes.string,
  profile: PropTypes.object.isRequired,
  goto: PropTypes.func.isRequired,
  editBooking: PropTypes.func.isRequired,
};

PlayerBookings.defaultProps = {
  token: null,
};

const mapStateToProps = ({login: {token, profile}, player: {bookings, partners}}) => ({
  token,
  profile,
  bookings,
  partners
});

const mapDispatchToProps = dispatch => ({
  goto: (path, action = null) => e => {
    e.preventDefault();
    if (action) {
      dispatch(action());
    }

    dispatch(push(path));
  },
  deleteBooking: (bookingId, partnerId, isService, multiBooking, phoneNumber) => {
    if(isService) dispatch(deleteServiceBooking(bookingId, partnerId, multiBooking, phoneNumber));
    else dispatch(deleteBooking(bookingId, partnerId, multiBooking, phoneNumber));
  },
  purchaseVideo: slots => {
    dispatch(purchaseVideo(slots));
  },
  editBooking: booking => dispatch(openBookingForEdit(booking)),
});

export default withRouter(connect(
  mapStateToProps,
  mapDispatchToProps
)(PlayerBookings));
